import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { ar } from "date-fns/locale";
import { Clock, User, Phone, Mail, ContactRound, MessageCircle } from "lucide-react";
import { whatsappUrl } from "@/lib/phone";

const fmtTime = (iso) => new Date(iso).toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit", hour12: false });

const STATUS_TINT = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  completed: "bg-stone-200 text-stone-700",
};

const STATUS_AR = {
  pending: "بانتظار الدفع",
  confirmed: "مؤكدة",
  completed: "منتهية",
};

const AdminTasks = ({ onOpenClient }) => {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const [counts, setCounts] = useState({});
  const [selected, setSelected] = useState(new Date());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get("/admin/tasks/calendar", { params: month })
      .then((r) => setCounts(r.data.counts || {}))
      .catch(() => setCounts({}));
  }, [month]);

  const loadDay = async (d) => {
    setLoading(true);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const date_str = `${y}-${m}-${day}`;
    try {
      const { data } = await api.get("/admin/tasks/day", { params: { date_str } });
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selected) loadDay(selected);
  }, [selected]);

  const totalThisMonth = useMemo(
    () => Object.values(counts).reduce((a, b) => a + b, 0),
    [counts]
  );

  // Custom day modifier renderer that adds a session-count badge
  const renderDay = (day) => {
    const y = day.getFullYear();
    const m = String(day.getMonth() + 1).padStart(2, "0");
    const dd = String(day.getDate()).padStart(2, "0");
    const key = `${y}-${m}-${dd}`;
    const c = counts[key];
    return (
      <div className="relative flex flex-col items-center justify-center w-full h-full">
        <span>{day.getDate()}</span>
        {c > 0 && (
          <span
            data-testid={`tasks-day-badge-${key}`}
            className="absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#0A4D3C] text-white text-[10px] flex items-center justify-center font-medium"
          >
            {c}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="card-warm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl text-[#222222]">تقويم الجلسات</h3>
          <Badge className="rounded-full bg-[#F6F4EE] text-[#0A4D3C]">
            هذا الشهر: {totalThisMonth}
          </Badge>
        </div>
        <div
          className="bg-[#F6F4EE] rounded-2xl p-2 inline-block"
          style={{ direction: "ltr" }}
        >
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => d && setSelected(d)}
            onMonthChange={(m) =>
              setMonth({ year: m.getFullYear(), month: m.getMonth() + 1 })
            }
            weekStartsOn={0}
            showOutsideDays={true}
            locale={ar}
            dir="ltr"
            data-testid="admin-tasks-calendar"
            components={{
              DayContent: ({ date }) => renderDay(date),
            }}
          />
        </div>
      </div>

      <div className="card-warm p-6">
        <h3 className="font-heading text-xl text-[#222222]">
          جلسات اليوم{" "}
          {selected && (
            <span className="text-sm text-[#5E5C58]">
              ({selected.toLocaleDateString("ar-SA-u-ca-gregory", { calendar: "gregory", weekday: "long", day: "numeric", month: "long" })})
            </span>
          )}
        </h3>
        <div className="mt-4 space-y-3 max-h-[520px] overflow-auto pe-1" data-testid="admin-tasks-day-list">
          {loading && <p className="text-[#5E5C58] text-sm">جاري التحميل...</p>}
          {!loading && items.length === 0 && (
            <p className="text-[#5E5C58] text-sm">لا توجد جلسات في هذا اليوم.</p>
          )}
          {items.map((b) => (
            <div
              key={b.id}
              data-testid={`admin-tasks-item-${b.id}`}
              className="rounded-2xl border border-[#E5E1D8] bg-white p-4"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-[#0A4D3C] font-heading text-lg">
                  <Clock className="w-4 h-4" />
                  {fmtTime(b.slot_iso)}
                </div>
                <Badge className={`rounded-full ${STATUS_TINT[b.status] || "bg-stone-100"}`}>
                  {STATUS_AR[b.status] || b.status}
                </Badge>
              </div>
              <div className="mt-2 font-medium text-[#222222]">{b.service_title_ar}</div>
              <div className="mt-2 text-sm text-[#5E5C58] flex flex-wrap gap-x-4 gap-y-1">
                {onOpenClient ? (
                  <button
                    type="button"
                    data-testid={`admin-tasks-open-client-${b.id}`}
                    onClick={() => onOpenClient(b)}
                    title="فتح ملف العميل"
                    className="inline-flex items-center gap-1 text-[#0A4D3C] hover:underline focus:outline-none focus:ring-1 focus:ring-[#0A4D3C] rounded"
                  >
                    <ContactRound className="w-3.5 h-3.5" />
                    {[b.user_first_name, b.user_middle_name, b.user_last_name].filter(Boolean).join(" ") || b.user_name || "—"}
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />{" "}
                    {[b.user_first_name, b.user_middle_name, b.user_last_name].filter(Boolean).join(" ") || b.user_name || "—"}
                  </span>
                )}
                {b.user_email && (
                  <span className="inline-flex items-center gap-1" dir="ltr">
                    <Mail className="w-3.5 h-3.5" /> {b.user_email}
                  </span>
                )}
                {b.user_phone && (() => {
                  const wa = whatsappUrl(b.user_phone);
                  return wa ? (
                    <a
                      href={wa}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="فتح واتساب"
                      data-testid={`admin-tasks-whatsapp-${b.id}`}
                      className="inline-flex items-center gap-1 text-[#1ebe5b] hover:underline"
                      dir="ltr"
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> {b.user_phone}
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[#1ebe5b]" dir="ltr">
                      <Phone className="w-3.5 h-3.5" /> {b.user_phone}
                    </span>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminTasks;
